"use client";

import { useT } from "@/lib/i18n/context";
import type { WShopBestsellerProductsContent } from "@/lib/api/cms-window-content";
import { ProductCard } from "@/components/ui/ProductCard/ProductCard";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import Link from "next/link";
import { wShopBestsellerProductsTranslations } from "./WShopBestsellerProducts.i18n";
import styles from "./WShopBestsellerProducts.module.css";

/**
 * WShopBestsellerProducts - okno "najczesciej kupowane produkty".
 *
 * Grid kafelkow produktow (reuse `<ProductCard />` z ui/), naglowek sekcji z
 * tytulem instancji okna + opcjonalny przycisk "zobacz wszystkie" gdy admin
 * ustawil `config[list_url]`. Fallback title z i18n gdy admin nie nazwal
 * instancji (`content.title` puste).
 *
 * Rendering identyczny z pozostalymi oknami sklepowymi (promotional / latest /
 * recomended) - roznica wylacznie w warstwie danych (backend filtruje po fladze
 * `bestseller=1`). Kafelek i grid sa te same (`<ProductCard />`).
 *
 * Data source: `W_SHOP_BESTSELLER_PRODUCTS_CONTROLLER::getApiContent()`.
 */
export function WShopBestsellerProducts({
  content,
  title,
}: WShopBestsellerProductsProps) {
  const t = useT(wShopBestsellerProductsTranslations);

  if (!content) return null;

  const headingText = title || content.title || t.title;
  const showAllHref = content.href ? extractInternalHref(content.href) : null;

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        <h2 className={styles.heading}>{headingText}</h2>
        {content.href && (
          showAllHref !== null ? (
            <Link href={showAllHref} className={styles.showAllBtn}>
              {t.showAll}
            </Link>
          ) : (
            <a href={content.href} className={styles.showAllBtn}>
              {t.showAll}
            </a>
          )
        )}
      </div>

      {content.items.length === 0 ? (
        <div className={styles.empty}>{t.noProducts}</div>
      ) : (
        <ul className={styles.productList}>
          {content.items.map((item) => (
            <ProductCard
              key={item.id}
              item={item}
              currency={content.currency}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export type WShopBestsellerProductsProps = {
  content: WShopBestsellerProductsContent | null;
  title: string;
};
